import { ColDef, GridOptions } from 'ag-grid-enterprise';

import {
  buildColDef,
  ColType,
  columnSize,
} from '@app/shared/utils/ag-grid.col-def.utils';

import { CostLayerChangesAgCellSummaryComponent } from '../components/cost-layer-changes-ag-cell-summary/cost-layer-changes-ag-cell-summary.component';
import { CostLayerChangesAgCellTransactionComponent } from '../components/cost-layer-changes-ag-cell-transaction/cost-layer-changes-ag-cell-transaction.component';
import { ECostLayerChangesLogLayout } from '../cost-layers.const';

export enum ECostLayerChangesLogCol {
  DATETIME_CREATED = 'DATETIME_CREATED',
  DATETIME_UPDATED = 'DATETIME_UPDATED',
  QUANTITY_CHANGE = 'QUANTITY_CHANGE',
  REMARKS = 'REMARKS',
  SUMMARIZED_CHANGES = 'SUMMARIZED_CHANGES',
  TOTAL_AFTER_CHANGE = 'TOTAL_AFTER_CHANGE',
  TOTAL_REMAINING_AFTER_CHANGE = 'TOTAL_REMAINING_AFTER_CHANGE',
  TRANSACTION = 'TRANSACTION',
  UNIT_COST_CHANGE = 'UNIT_COST_CHANGE',
}

export const CostLayerChangesLogColToField :{
  [key in ECostLayerChangesLogCol] ?:string;
} = {
  [ECostLayerChangesLogCol.DATETIME_CREATED]: 'createdAt',
  [ECostLayerChangesLogCol.DATETIME_UPDATED]: 'updatedAt',
  [ECostLayerChangesLogCol.QUANTITY_CHANGE]: 'quantityChange',
  [ECostLayerChangesLogCol.REMARKS]: 'remarks',
  [ECostLayerChangesLogCol.TOTAL_AFTER_CHANGE]: 'totalCostAfterChange',
  [ECostLayerChangesLogCol.TOTAL_REMAINING_AFTER_CHANGE]:
    'remainingQuantityCostAfterChange',
  [ECostLayerChangesLogCol.TRANSACTION]: 'transaction',
  [ECostLayerChangesLogCol.UNIT_COST_CHANGE]: 'costPerUnitChange',
} as const;

const _colIdentity = (colId :ECostLayerChangesLogCol) => ({
  colId,
  field: CostLayerChangesLogColToField[colId] ?? null,
});

export const CostLayerChangesLogColDef = {
  [ECostLayerChangesLogCol.DATETIME_CREATED]: buildColDef(ColType.DATETIME, {
    ..._colIdentity(ECostLayerChangesLogCol.DATETIME_CREATED),
    headerName: 'Time',
    ...columnSize(15),
  }),
  [ECostLayerChangesLogCol.DATETIME_UPDATED]: buildColDef(ColType.DATETIME, {
    ..._colIdentity(ECostLayerChangesLogCol.DATETIME_UPDATED),
    headerName: 'Last Updated',
    ...columnSize(15),
  }),
  [ECostLayerChangesLogCol.QUANTITY_CHANGE]: buildColDef(ColType.QUANTITY, {
    ..._colIdentity(ECostLayerChangesLogCol.QUANTITY_CHANGE),
    headerClass: 'text-right',
    headerName: 'Qty Change',
    ...columnSize(12),
  }),
  [ECostLayerChangesLogCol.REMARKS]: buildColDef(
    ColType.DEFAULT,
    {
      ..._colIdentity(ECostLayerChangesLogCol.REMARKS),
      headerName: 'Notes',
      ...columnSize(24, 240),
    },
    {
      multiline: true,
    },
  ),
  [ECostLayerChangesLogCol.TOTAL_AFTER_CHANGE]: buildColDef(ColType.CURRENCY, {
    ..._colIdentity(ECostLayerChangesLogCol.TOTAL_AFTER_CHANGE),
    headerClass: 'text-right',
    headerName: 'Original Total Cost',
    ...columnSize(10),
  }),
  [ECostLayerChangesLogCol.TOTAL_REMAINING_AFTER_CHANGE]: buildColDef(
    ColType.CURRENCY,
    {
      ..._colIdentity(ECostLayerChangesLogCol.TOTAL_REMAINING_AFTER_CHANGE),
      headerClass: 'text-right',
      headerName: 'Total (Remaining)',
      ...columnSize(12),
    },
  ),
  [ECostLayerChangesLogCol.SUMMARIZED_CHANGES]: buildColDef(ColType.DEFAULT, {
    ..._colIdentity(ECostLayerChangesLogCol.SUMMARIZED_CHANGES),
    headerName: 'Change Summary',
    sortable: false,
    ...columnSize(24),
    cellRenderer: CostLayerChangesAgCellSummaryComponent,
  }),
  [ECostLayerChangesLogCol.TRANSACTION]: buildColDef(ColType.DEFAULT, {
    ..._colIdentity(ECostLayerChangesLogCol.TRANSACTION),
    headerName: 'Record',
    sortable: false,
    ...columnSize(20),
    cellRenderer: CostLayerChangesAgCellTransactionComponent,
  }),
  [ECostLayerChangesLogCol.UNIT_COST_CHANGE]: buildColDef(ColType.CURRENCY, {
    ..._colIdentity(ECostLayerChangesLogCol.UNIT_COST_CHANGE),
    headerClass: 'text-right',
    headerName: 'Unit Cost Change',
    ...columnSize(14),
  }),
};

export const CostLayerChangesLogColDefs :{ [key :string] :ColDef[] } = {
  [ECostLayerChangesLogLayout.AUDIT]: [
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.DATETIME_CREATED],
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.TRANSACTION],
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.SUMMARIZED_CHANGES],
  ],
  [ECostLayerChangesLogLayout.SALES]: [
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.DATETIME_CREATED],
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.TRANSACTION],
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.QUANTITY_CHANGE],
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.UNIT_COST_CHANGE],
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.TOTAL_REMAINING_AFTER_CHANGE],
    CostLayerChangesLogColDef[ECostLayerChangesLogCol.REMARKS],
  ],
};

export const COST_LAYER_CHANGES_LOG_DEFAULT_GRID_OPTIONS :GridOptions = {
  cacheBlockSize: 10,
  domLayout: 'normal',
  pagination: true,
  paginationPageSize: 10,
  rowModelType: 'serverSide',
};
